/** Local NeuroQuery inference. Caller owns assets and display; no network or DOM. */
export function createTextEncoder(header,lexicon,arrays) {
  if(header.format!=='ba-mind-text-model-v1')throw new Error('Unsupported text model');
  const {semantic,coefficients,variance_gram:gram,voxel_indices:indices}=arrays;
  const {vocabulary_size:n,features:d,voxels:p}=header;
  for(const [value,length] of [[semantic,n*d],[coefficients,p*d],[gram,d*d]]) {
    if(!(value instanceof Float32Array)||value.length!==length)throw new Error('Malformed model array');
    if(value.some(x=>!Number.isFinite(x)))throw new Error('Nonfinite model weight');
  }
  if(!(indices instanceof Uint32Array)||indices.length!==p)throw new Error('Malformed voxel order');
  if(lexicon.vocabulary.length!==n||lexicon.idf.length!==n)throw new Error('Malformed lexicon');
  const stop=new Set(lexicon.stop_words),trie=new Map();
  for(const [phrase,index] of Object.entries(lexicon.phrases)) {
    let node=trie;
    for(const word of phrase.split(' ')){if(!node.has(word))node.set(word,new Map());node=node.get(word);}
    node.set('',index);
  }
  function tokenize(text) {
    if(typeof text!=='string')throw new TypeError('Expected text');
    const words=(text.match(/(?:(?:\p{L}|\p{N}){2,}|[IVXivx0-9])/gu)||[])
      .map(w=>w.toLowerCase()).filter(w=>!stop.has(w));
    const counts=new Map();
    for(let i=0;i<words.length;){
      let node=trie,last=-1,end=i;
      for(let j=i;j<words.length&&node.has(words[j]);j++){
        node=node.get(words[j]);
        if(node.has('')){last=node.get('');end=j+1;}
      }
      if(last>=0){counts.set(last,(counts.get(last)||0)+1);i=end;}else i++;
    }
    return [...counts].map(([index,count])=>({index,count,term:lexicon.vocabulary[index]}))
      .sort((a,b)=>a.term<b.term?-1:a.term>b.term?1:0);
  }
  function encode(text) {
    const terms=tokenize(text);
    if(!terms.length)return {status:'unrecognized',terms,map:null};
    const features=new Float64Array(d);
    const norm=Math.hypot(...terms.map(t=>t.count*lexicon.idf[t.index]));
    if(!(norm>0))throw new Error('Invalid text normalization');
    for(const t of terms){
      const weight=t.count*lexicon.idf[t.index]/norm,offset=t.index*d;
      for(let j=0;j<d;j++)features[j]+=weight*semantic[offset+j];
    }
    let variance=0;
    for(let j=0;j<d;j++){
      let row=0;for(let k=0;k<d;k++)row+=gram[j*d+k]*features[k];
      variance+=features[j]*row;
    }
    if(!(variance>0)||!Number.isFinite(variance))throw new Error('Invalid prediction variance');
    const denominator=Math.sqrt(variance),map=new Float32Array(p);
    for(let v=0;v<p;v++){
      let sum=0;for(let j=0;j<d;j++)sum+=features[j]*coefficients[v*d+j];
      map[v]=sum/denominator;
    }
    return {status:'encoded',terms,map};
  }
  return Object.freeze({encode,tokenize,header,voxelIndices:indices});
}

/** Fetch/hash-check static model assets once. encode() itself never fetches. */
export async function loadTextEncoder(base,{fetch:request=globalThis.fetch}={}) {
  const url=new URL(base,globalThis.location?.href);
  async function read(name,descriptor){
    const response=await request(new URL(name,url));if(!response.ok)throw new Error(`Model fetch failed: ${name}`);
    const bytes=await response.arrayBuffer();
    if(descriptor){
      const hash=[...new Uint8Array(await crypto.subtle.digest('SHA-256',bytes))].map(x=>x.toString(16).padStart(2,'0')).join('');
      if(bytes.byteLength!==descriptor.byte_length||hash!==descriptor.sha256)throw new Error(`Model integrity failed: ${name}`);
    }
    return bytes;
  }
  const header=JSON.parse(new TextDecoder().decode(await read('header.json')));
  const lexicon=JSON.parse(new TextDecoder().decode(await read(header.lexicon.path,header.lexicon)));
  const arrays=Object.fromEntries(await Promise.all(Object.entries(header.arrays).map(async([name,desc])=>{
    const bytes=await read(desc.path,desc);
    const Type=desc.dtype==='<f4'?Float32Array:desc.dtype==='<u4'?Uint32Array:null;
    if(!Type)throw new Error('Unsupported array encoding');
    return [name,new Type(bytes)];
  })));
  return createTextEncoder(header,lexicon,arrays);
}
